import {FC, useState, useEffect} from 'react'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {useAppSelector} from '../../../../setup/redux/hooks'
import {BranchModel} from '../models/BranchModel'
import {getBranchUsers, selectBranchUsersList} from '../redux/BranchRedux'
import { USER_ROLE, USER_STATUS } from '../../../shared/constants'

type Props = {
  className?: string
  onClose: () => void
  onConfirm: (personInChargeId: number) => void
  isShow: boolean
  currentBranch: BranchModel | null
}

const ActivateBranchModal: FC<Props> = (props) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const availableUsers = useAppSelector(selectBranchUsersList)

  // Fetch available users when modal opens
  useEffect(() => {
    if (props.isShow) {
      dispatch(getBranchUsers())
    }
  }, [props.isShow, dispatch])

  const onClose = () => {
    setSelectedUserId(null)
    props.onClose()
  }

  const onConfirm = () => {
    if (selectedUserId) {
      props.onConfirm(selectedUserId)
      setSelectedUserId(null)
    }
  }

  const handleUserSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const userId = parseInt(event.target.value)
    setSelectedUserId(userId || null)
  }

  // Filter users to show only those who can be assigned as person in charge
  const eligibleUsers = availableUsers.filter(
    (user) => user.status === USER_STATUS.ACTIVE && user.role === USER_ROLE.BRANCH_EMPLOYEE // Assuming active users only
  )

  return (
    <Modal
      size='lg'
      aria-labelledby='contained-modal-title-vcenter'
      centered
      show={props.isShow}
      onHide={onClose}
      backdrop='static'
    >
      <Modal.Header closeButton>
        <p className='card-label fw-bolder fs-1 mb-1'>
          {intl.formatMessage({id: 'BRANCH.ACTIVATE.TITLE'})}
        </p>
      </Modal.Header>
      <Modal.Body>
        <div className='mb-4'>
          <p>{intl.formatMessage({id: 'BRANCH.ACTIVATE.MESSAGE'})}</p>
          {props.currentBranch && (
            <p className='text-muted'>
              <strong>{intl.formatMessage({id: 'BRANCH.NAME'})}:</strong> {props.currentBranch.name}
            </p>
          )}
        </div>
        
        <div className='mb-4'>
          <Form.Label className='required fw-bold'>
            {intl.formatMessage({id: 'BRANCH.ACTIVATE.SELECT_USER'})}
          </Form.Label>
          <Form.Select
            value={selectedUserId || ''}
            onChange={handleUserSelect}
            required
          >
            <option value=''>
              {intl.formatMessage({id: 'BRANCH.ACTIVATE.SELECT_PLACEHOLDER'})}
            </option>
            {eligibleUsers.map((user) => (
              <option key={user.id} value={user.id}>
                {user.firstName} {user.lastName} - {user.email}
              </option>
            ))}
          </Form.Select>
        </div>

        {eligibleUsers.length === 0 && (
          <div className='alert alert-warning'>
            <i className='bi bi-exclamation-triangle-fill me-2'></i>
            {intl.formatMessage({id: 'BRANCH.ACTIVATE.NO_USERS_WARNING'})}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className='d-flex justify-content-end flex-row-fluid'>
          <Button
            type='button'
            variant='secondary'
            onClick={onClose}
            className='btn btn-light me-3'
          >
            {intl.formatMessage({id: 'BRANCH.ACTIVATE.CANCEL'})}
          </Button>

          <Button 
            type='button' 
            variant='success' 
            onClick={onConfirm}
            disabled={!selectedUserId || eligibleUsers.length === 0}
          >
            {intl.formatMessage({id: 'BRANCH.ACTIVATE.CONFIRM'})}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default ActivateBranchModal
